import { useContext, useMemo } from 'react';
import Error from './ErrorBoundary';
import Pagination from './Pagination';
import ProductCard from './ProductCard';
import SortView from './filters/SortView';
import SearchFiltersOffcanvas from './filters/SearchFiltersOffcanvas';
import AppliedFilters from './filters/AppliedFilters';
import { ProductsContext, ViewContext, CatalogStateContext } from '../store/CatalogContext';
import type { Product } from '../types/product';
import SearchFiltersTopbar from './filters/SearchFiltersTopbar';
import useSearchAndFilter from '../hooks/useSearchAndFilter';
import ProductCardSkeletonGroup from './loading-skeletons/ProductCardSkeletonGroup';
// import useFuzzySearch from '../hooks/useFuzzySearch';

interface CuratedCatalogProps {
  group: 'purchased' | 'related' | 'fullCatalog';
  prefix: string;
  gridCount: 1 | 2 | 3 | 4;
}

export default function CuratedCatalog({ group, prefix, gridCount }: CuratedCatalogProps) {
  const productsState = useContext(ProductsContext);
  const viewState = useContext(ViewContext);
  const catalogState = useContext(CatalogStateContext); // for flags like isLoading (could also extract isLoading separately)

  const curatedProducts = useMemo(() => productsState[group] || [], [productsState, group]);

  // view state from context
  const {
    pageNumber,
    productPerPage,
    sort,
    filters: { searchQuery, collection, category, subCategory, individualBundle },
    grid: currentGrid,
  } = viewState[group];

  // search results, this needs to be the top level set of products
  // const { results: searchedProducts } = useFuzzySearch(curatedProducts, searchQuery ? searchQuery : '');

  // use filtered products hooks to get the final product list
  const { results: resultProducts, availableFilters } = useSearchAndFilter(curatedProducts, {
    searchQuery,
    collection,
    category,
    subCategory,
    individualBundle,
    sort,
  });

  const curatedFilters = useMemo(
    () => ({
      collection: availableFilters.collection,
      category: availableFilters.category,
      subCategory: availableFilters.subCategory,
      individualBundle: availableFilters.individualBundle,
      showBundleFilters:
        availableFilters.individualBundle.length === 2 &&
        availableFilters.individualBundle.every((filter) => filter.matching > 0),
    }),
    [availableFilters]
  );

  // paginate after sorting
  const pageProducts = useMemo(() => {
    const start = (pageNumber - 1) * productPerPage;
    return resultProducts.slice(start, start + productPerPage);
  }, [resultProducts, pageNumber, productPerPage]);

  // memoize mapping to avoid remapping unless slice changes
  const productCards = useMemo(
    () => pageProducts.map((product: Product) => <ProductCard key={product.id} product={product} />),
    [pageProducts]
  );

  return (
    <>
      <div className="row gx-2 mb-3 mb-lg-4 flex align-items-center">
        {/* Scroll sentinel for pagination resets */}
        <div id={`${group}-top`} aria-hidden="true" />

        <div className="d-none d-lg-block">
          <SearchFiltersTopbar group={group} prefix={prefix} filters={curatedFilters} filteredProducts={resultProducts} />
        </div>

        <SearchFiltersOffcanvas
          group={group}
          prefix={`slide-${prefix}`}
          filters={curatedFilters}
          filteredProducts={resultProducts}
        />

        <div className="col-12 col-sm-6 col-md-4 col-lg-3 d-none d-sm-block d-lg-none">
          <SortView group={group} prefix={`sort-${prefix}`} />
        </div>
      </div>

      <AppliedFilters group={group} />

      <div className={`row product-list-display grid-col-${currentGrid || gridCount} gx-3 gx-xl-4`}>
        {catalogState.isLoading ? (
          <ProductCardSkeletonGroup count={6} />
        ) : (
          <>
            {productCards.length > 0 ? (
              productCards
            ) : (
              <div className="col-12 py-4 text-center text-muted">
                No products found. Try clearing your filters if you have one applied.
              </div>
            )}
          </>
        )}
      </div>

      <Pagination group={group} itemsLength={resultProducts.length} />
    </>
  );
}

export const ErrorBoundary = () => {
  return <Error fromPage="Curated Catalog Component" />;
};
